/* eslint-disable arrow-body-style */
import React, { useState } from 'react';
import { Form, Input, Button } from 'antd';
import axios from 'axios';
import './Section508.scss';

const { TextArea } = Input;

const Section508 = () => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const onFinish = async (values) => {
        setSubmitting(true);
        setResult(null);
        try {
            const apiUrl = `${process.env.REACT_APP_API_HOST}/rest/api/section508`;
            const response = await axios.post(apiUrl, values, {
                headers: { 'Content-Type': 'application/json' }
            });
            const data = response.data;
            if (data.success) {
                setResult({ type: 'success', message: 'Your issue has been submitted. Thank you!' });
                form.resetFields();
            } else {
                setResult({ type: 'error', message: data.error || 'Submission failed.' });
            }
        } catch (err) {
            // Improved error logging
            console.error(JSON.stringify({
                message: 'Failed to send Section 508 email',
                error: err && err.message ? err.message : err,
                sendgridKeySet: !!process.env.SENDGRID_API_KEY_508,
                eventBody: values,
                envStage: process.env.STAGE || process.env.NODE_ENV,
                time: new Date().toISOString()
            }));
            setResult({ type: 'error', message: 'Network or server error.' });
        }
        setSubmitting(false);
    };

    return (
        <div className="section508-container">
            <h1>Section 508 Accessibility Statement & Issues & Concern Form</h1>
            <section>
                <h2>Accessibility Statement</h2>
                <p>
                    The Non-Ferrous Founders Society (NFFS) is committed to making its electronic and information technologies (ICON) portal accessible to individuals with disabilities in accordance with Section 508 of the Rehabilitation Act (29 U.S.C. 794d), as amended in 1998. DoD websites use the WCAG 2.0 AA accessibility standard.
                </p>
                <h3>Digital Accessibility Officer</h3>
                <p>Sheila Rayburn<br />ICON <a href="mailto:support@iconportal.com?bcc=support@nffs.atlassian.net&subject=Section%20508%20Accessibility%20Issue%20Report&body=Please%20fill%20out%20the%20following%20form%20to%20report%20a%20Section%20508%20accessibility%20issue%3A%0A%0AFirst%20Name%3A%20%0ALast%20Name%3A%20%0AEmail%20Address%3A%20%0AComponent%20Name%3A%20%0AOrg%20Name%3A%20%0A%0ADescription%20of%20Section%20508-related%20issue%20or%20concern%3A%0A%0A%0A%0ANote%3A%20Do%20not%20include%20any%20personal%20or%20personally%20identifiable%20data%2C%20including%20your%20specific%20disability%2C%20Social%20Security%20number%20or%20date%20of%20birth.">Section 508 Mailbox</a></p>
                <h3>Reporting accessibility problems</h3>
                <p>
                    For persons with disabilities experiencing difficulties accessing content on a particular website, please use the form below. In this form, please indicate the nature of your accessibility issue/problem and your contact information so we can address your issue or question. If your issue involves login access, password recovery, or other technical issues, contact the administrator for the website in question or your local helpdesk.
                </p>
                <h3>Formal complaints alleging a violation of Section 508</h3>
                <p>
                    ICON / NFFS does not have a department-wide policy or process for formal Section 508 complaints of discrimination. If you wish to file such a complaint, please contact your component Office of Civil Rights or Equal Employment Opportunity office, or your component Disability Program Manager for instructions.
                </p>
                <h3>Reasonable Accommodations</h3>
                <p>
                    ICON / NFFS does not have a department-wide policy or process for Reasonable Accommodations. If you are seeking a reasonable accommodation for a disability, contact your component Disability Program Manager. The DoD Computer/Electronic Accommodations Program (CAP) provides assessments for equipment or licenses for assistive technologies for employees. CAP can be reached at: <a href="https://cap.mil/" target="_blank" rel="noopener noreferrer">https://cap.mil/</a>
                </p>
                <h3>FCC TRS</h3>
                <p>
                    The Federal Communications Commission (FCC) Telecommunications Relay Service (TRS) is available here: <a href="https://www.fcc.gov/trs" target="_blank" rel="noopener noreferrer">https://www.fcc.gov/trs</a>
                </p>
                <h3>Digital Accessibility Policy</h3>
                <p>
                    The DoD policy for digital accessibility is available at <a href="https://www.esd.whs.mil/Portals/54/Documents/DD/issuances/dodm/840001_dodm_2017.pdf" target="_blank" rel="noopener noreferrer">this link</a>.<br />
                    For more information about Section 508, please visit the <a href="https://www.section508.gov/" target="_blank" rel="noopener noreferrer">DoD Section 508 page</a>.
                </p>
            </section>
            <section>
                <h2>Section 508 Issues and Concerns Form</h2>
                <div className="alert-container">
                    <div className="alert-content">
                        <b>IMPORTANT</b>: This form is for <b>INDIVIDUALS WITH DISABILITIES</b> who are having difficulty accessing Department of Defense information and communications technologies, according to Section 508 of the Rehabilitation Act, as amended in 1998.
                    </div>
                </div>
                <div className="required-fields-note">
                    <span className="required-indicator">* Asterisk *</span> Indicates a required field. All required fields must be filled out to submit the form.
                </div>
                <Form form={form} layout="vertical" onFinish={onFinish} className="section508-form">
                    <Form.Item
                        label={<span className="required-field">First Name</span>}
                        name="firstName"
                        rules={[{ required: true, message: 'First name is required' }]}
                        className="form-item"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={<span className="required-field">Last Name</span>}
                        name="lastName"
                        rules={[{ required: true, message: 'Last name is required' }]}
                        className="form-item"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={<span className="required-field">Email Address</span>}
                        name="email"
                        rules={[{ required: true, message: 'Email is required', type: 'email' }]}
                        className="form-item"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Component Name"
                        name="componentName"
                        className="form-item"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Org Name"
                        name="orgName"
                        className="form-item"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={<span className="required-field">Description of Section 508-related issue or concern</span>}
                        name="description"
                        rules={[{ required: true, message: 'Description is required' }]}
                        className="form-item"
                    >
                        <TextArea rows={4} />
                    </Form.Item>
                    <Form.Item className="submit-item">
                        <Button type="primary" htmlType="submit" loading={submitting} className="submit-button">Submit</Button>
                    </Form.Item>
                </Form>
                <div className="section508-note">
                    <strong>Note:</strong> The information that you provide on this form is not entered into a system of records and will only be used to respond to your reported issue. Information will only be shared with another government agency if your inquiry relates to that agency, or as otherwise required by law. Your information will not be given to any non-government organizations or used for commercial marketing. While you must provide an e-mail address for a response to your Section 508 issue, do not include any other personal or personally identifiable data, including your specific disability, Social Security number or date of birth.
                </div>
                {result && (
                    <div className={`submission-result ${result.type}`}>
                        {result.message}
                    </div>
                )}
            </section>
        </div>
    );
};

export default Section508; 


// WEBPACK FOOTER //
// ./src/pages/static/Section508/Section508.jsx